// API configuration
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
export const API_VERSION = 'v1';

// Full API URL
export const API_URL = `${API_BASE_URL}/api/${API_VERSION}`;

// Helper function for API calls
export const apiUrl = (endpoint: string): string => {
  // Remove leading slash if present to avoid double slashes
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${API_URL}/${cleanEndpoint}`;
};

// Custom error class for API errors
export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Authenticated fetch function with automatic login redirect
export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include', // Include cookies for authentication
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    let errorDetails = null;

    try {
      const errorData = await response.json();
      if (errorData.message) {
        errorMessage = errorData.message;
      }
      if (errorData.detail) {
        errorDetails = errorData.detail;
      }
    } catch {
      // If we can't parse the error response, use the default message
    }

    // Handle authentication errors - redirect to login
    if (response.status === 401) {
      console.warn('[Auth] Authentication failed, redirecting to login...');

      // Clear any stored auth state
      localStorage.removeItem('user');
      localStorage.removeItem('authToken');

      // Show user-friendly message
      const message = 'Your session has expired. Redirecting to login...';

      // Small delay to let user see the message, then redirect
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);

      // Throw a user-friendly error
      throw new ApiError(message, response.status, errorDetails);
    }

    throw new ApiError(errorMessage, response.status, errorDetails);
  }

  return response;
}
